import React, { Suspense } from 'react';

import ChartBoundary from './ChartBoundary';

/**
 * THE LAZY BOUNDARY of the charts chunk (plan §6.4). This is the one module
 * of `src/components/charts/` that code outside the folder may import, and it
 * imports nothing of the chunk statically: each component below is a
 * React.lazy over `import('./chunk')`, which webpack emits as one file. A
 * page that never draws a chart never downloads it.
 *
 * Each export suspends with a quiet frame of the chart's own height, so a
 * page does not jump when the chunk arrives, and sits inside a ChartBoundary
 * (React only, no chunk code), so a chunk that fails to download — an open
 * tab after a deploy replaced the hashed file — says so in the chart's own
 * frame instead of taking the page down.
 */
const load = () => import(/* webpackChunkName: "charts" */ './chunk');

const LazyFrame = React.lazy(() => load().then((m) => ({ default: m.ChartFrame })));
const LazyTable = React.lazy(() => load().then((m) => ({ default: m.ChartTable })));
const LazySamples = React.lazy(() => load().then((m) => ({ default: m.ChartSamples })));

function Waiting({ height = 280 }) {
	return <div className='chart-frame__waiting chart-frame__waiting--chunk' style={{ height }} aria-busy='true' />;
}

/** One chart: title, form, notes, foot, error and empty state (ChartFrame). */
export function Chart(props) {
	return (
		<Suspense fallback={<Waiting height={props.height} />}>
			<ChartBoundary framed title={props.showTitle !== false && props.chart && typeof props.chart.name === 'string' ? props.chart.name : ''} resetKey={props.result} headingLevel={props.headingLevel}>
				<LazyFrame {...props} />
			</ChartBoundary>
		</Suspense>
	);
}

/** A result as its table twin alone (ChartTable). */
export function ChartTableLazy(props) {
	return (
		<Suspense fallback={<Waiting height={120} />}>
			<ChartBoundary resetKey={props.table}>
				<LazyTable {...props} />
			</ChartBoundary>
		</Suspense>
	);
}

/** One sample of each form, for the admin style guide (ChartSamples). */
export function ChartSamplesLazy(props) {
	return (
		<Suspense fallback={<Waiting height={240} />}>
			<ChartBoundary>
				<LazySamples {...props} />
			</ChartBoundary>
		</Suspense>
	);
}

/** Start downloading the chunk before it is drawn (a page about to show charts). */
export const preloadCharts = load;

/**
 * Share & export (A9, chartExport.js): `{ saveChartData, saveChartImage }`,
 * from the same chunk — Download data's file and Download image's picture are
 * drawn by the option builders and echarts, which never enter the main bundle.
 */
export const loadChartExport = () => load().then((m) => ({ saveChartData: m.saveChartData, saveChartImage: m.saveChartImage }));
